import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { track } from '../analytics/events.js';
import { scoreTextOn } from '../map/areaData.js';
import { scoreInk } from '../map/SearchScore.js';
import {
    COMPARE_ROWS, compareArea, compareTops, fetchAreaHomesTotal, fetchAreaTrend, formatScore, formatTrend,
} from '../map/compareData.js';
import { neighbourhoodKommuner } from '../map/neighbourhoodScoring.js';
import { saveAreaPagePick, saveAreaPagePicks } from '../map/savedSelection.js';
import { MAX_COMPARED, areaPagePath, comparePath, shareComparison } from '../map/areaShare.js';
import { noteComparedHomesShown } from '../map/compareAreas.js';
import useColumnDrag from '../map/useColumnDrag.js';
import { areaFeatures, areasMapShapes, findArea, loadAreaSources } from './areaSources.js';
import './areaPage.css';
import './comparePage.css';

// A comparison of areas at /jamfor/<code>,<code>… (2–MAX_COMPARED areas): the comparison card of the views
// (CompareCard.js, design A "Kolumner" of the 2026-11-01 "Jämföra områden" canvas) at page size, so a shared link
// opens what was compared. A hero with the areas numbered on one outline map and the way in to their homes (listvy
// or kartvy with them picked), then a column per area — its number, score, price trend, name and word — with the
// six numbers in rows (the best of each marked: Högst, Flest, Lägst) and "Visa N bostäder" for each; the page leans
// on that, since whoever opens it hasn't been on the site before. "Visa bostäder" is only here, not in the card
// (the owner's wish). Unfurls with the areas listed (the backend's og_share.py, /og/compare/<codes>). The views
// also show it in a window over them (ComparePreview.js: `codes` given, `preview`), where kartvy's map buttons pick
// the areas on that map (onShowOnMap) and listvy's "Visa alla bostäder" makes them its search (onShowHomes).
// Under the columns, "Betyget jämförs med": the kommuner each area's score is weighed against (its own and the ones
// around it, neighbourhoodScoring.js), one line for the areas that share them (the owner, 2026-11-01). A column can
// be dragged by its area's name to another place (useColumnDrag.js): the link (or, previewed, the comparison
// itself, onReorder) takes the new order, and the key and the map's numbers follow.

const COPIED_MS = 2200;
const CODE_RE = /^\d{4}R\d{3}$/;
// Names written out in the title; more are counted.
const NAMED_IN_TITLE = 4;

const SHARE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15V3" />
        <path d="M7.5 7.5L12 3l4.5 4.5" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
);

const joinNames = (names, swedish) => {
    if (names.length < 2) return names.join('');
    if (names.length > NAMED_IN_TITLE) {
        const rest = names.length - (NAMED_IN_TITLE - 1);
        return `${names.slice(0, NAMED_IN_TITLE - 1).join(', ')} ${swedish ? `och ${rest} till` : `and ${rest} more`}`;
    }
    return `${names.slice(0, -1).join(', ')} ${swedish ? 'och' : 'and'} ${names[names.length - 1]}`;
};

// Every name, the last joined with "och" / "and".
const listNames = (names, swedish) => (names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} ${swedish ? 'och' : 'and'} ${names[names.length - 1]}`);

// "Stockholms kommun", "Malmö kommun", "Borås kommun": an s after a consonant except s, x and z; two names don't
// follow it.
const KOMMUN_GENITIVE = { Falun: 'Falu', Kalmar: 'Kalmar' };
const kommunName = (name, swedish) => {
    if (!swedish) return `${name} Municipality`;
    const genitive = KOMMUN_GENITIVE[name] || (/[^aeiouyåäösxz]$/i.test(name) ? `${name}s` : name);
    return `${genitive} kommun`;
};

const useBoxSize = (ref) => {
    const [size, setSize] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const update = () => setSize((prev) => (prev && prev.w === element.clientWidth && prev.h === element.clientHeight
            ? prev : { w: element.clientWidth, h: element.clientHeight }));
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return size;
};

// The areas on one outline map, each in its score colour with its number.
function CompareMap({ shapes, areas, swedish, onPickOther }) {
    const ref = useRef(null);
    const size = useBoxSize(ref);
    if (!shapes) return <div className="area-page-map" aria-hidden="true" />;
    const scale = size ? Math.min(size.w / shapes.box.w, size.h / shapes.box.h) : 0;
    const offsetX = size ? (size.w - shapes.box.w * scale) / 2 : 0;
    const offsetY = size ? (size.h - shapes.box.h * scale) / 2 : 0;
    return (
        <div className="area-page-map compare-map" ref={ref}>
            <svg viewBox={shapes.viewBox} preserveAspectRatio="xMidYMid meet" role="img"
                aria-label={swedish ? `${joinNames(areas.map((area) => area.name), true)} på kartan` : `${joinNames(areas.map((area) => area.name), false)} on the map`}>
                {shapes.others.map((other) => (
                    <a key={other.code} href={areaPagePath(other.code, other.name)} className="area-page-map-other"
                        onClick={(event) => { event.preventDefault(); onPickOther(other); }}>
                        <title>{other.kommun ? `${other.name}, ${other.kommun}` : other.name}</title>
                        <path d={other.d} fillRule="evenodd" vectorEffect="non-scaling-stroke" />
                    </a>
                ))}
                {shapes.areas.map((shape) => {
                    const area = areas.find((item) => item.code === shape.code);
                    return (
                        <path key={shape.code} className="area-page-map-main" d={shape.d} fillRule="evenodd" vectorEffect="non-scaling-stroke"
                            style={{ fill: area?.colour, stroke: scoreInk(area?.colour || '#8f9a97', 'light') }} />
                    );
                })}
            </svg>
            {size && shapes.areas.map((shape) => {
                const index = areas.findIndex((item) => item.code === shape.code);
                if (index < 0) return null;
                return (
                    <span key={shape.code} className="compare-map-num"
                        style={{ left: offsetX + (shape.middle[0] - shapes.box.x) * scale, top: offsetY + (shape.middle[1] - shapes.box.y) * scale }}>
                        {index + 1}
                    </span>
                );
            })}
        </div>
    );
}

export default function ComparePage({
    languageState, codes: codesProp = null, preview = false, onShowOnMap = null, onShowHomes = null, onClosePreview = null,
    onReorder = null,
}) {
    const swedish = Boolean(languageState?.swedish);
    const { codes: paramCodes } = useParams();
    const rawCodes = codesProp ? codesProp.join(',') : paramCodes;
    const navigate = useNavigate();
    const codes = useMemo(() => {
        const list = String(rawCodes || '').toUpperCase().split(',').map((code) => code.trim()).filter((code) => CODE_RE.test(code));
        return [...new Set(list)].slice(0, MAX_COMPARED);
    }, [rawCodes]);
    // The set, not the order: a column dragged to another place doesn't fetch again or count as another view.
    const codesKey = [...codes].sort().join(',');

    const [sources, setSources] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [homes, setHomes] = useState({}); // code -> homes for sale (null: unknown)
    const [trends, setTrends] = useState({}); // code -> { change, startYear, full } | null
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoadError(false);
        loadAreaSources()
            .then((loaded) => { if (!cancelled) setSources(loaded); })
            .catch(() => { if (!cancelled) setLoadError(true); });
        return () => { cancelled = true; };
    }, [attempt]);

    useEffect(() => {
        let cancelled = false;
        setHomes({});
        setTrends({});
        codes.forEach((code) => {
            fetchAreaHomesTotal(code).then((total) => {
                if (!cancelled) setHomes((prev) => ({ ...prev, [code]: total }));
            });
            fetchAreaTrend(code).then((trend) => {
                if (!cancelled) setTrends((prev) => ({ ...prev, [code]: trend }));
            });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codesKey]);

    // A column dragged by its name: the areas show in the drag's order while it's held.
    const scrollRef = useRef(null);
    const columnDrag = useColumnDrag({
        codes,
        scrollRef,
        enabled: codes.length > 1,
        onReorder: (order) => {
            track('compare.reorder', { count: order.length, source: preview ? 'preview' : 'page' });
            if (onReorder) onReorder(order);
            else navigate(comparePath(order), { replace: true });
        },
    });
    const order = columnDrag.order;
    const areas = useMemo(() => {
        if (!sources) return [];
        return order.map((code) => {
            const found = findArea(sources.topology, code);
            if (!found) return null;
            const area = compareArea(code, found.name, sources.colorCache, sources.stats, swedish);
            return area ? { ...area, kommun: found.kommun } : null;
        }).filter(Boolean);
    }, [sources, order, swedish]);
    const areasKey = areas.map((area) => area.code).sort().join(',');
    const shapes = useMemo(
        () => (sources && areasKey ? areasMapShapes(sources.topology, areasKey.split(',')) : null),
        [sources, areasKey],
    );
    const tops = useMemo(() => compareTops(areas), [areas]);
    // What the scores are weighed against: per area its kommun and the kommuner around it, the areas that share
    // them on one line (areas of one kommun always do), each with the numbers of its areas.
    const scoreBases = useMemo(() => {
        if (!sources) return [];
        const kommunNames = new Map();
        areaFeatures(sources.topology).forEach((item) => {
            const kommun = item.code.slice(0, 4);
            if (item.kommun && !kommunNames.has(kommun)) kommunNames.set(kommun, item.kommun);
        });
        const bases = new Map();
        areas.forEach((area, index) => {
            if (area.score == null) return;
            const [own, ...around] = neighbourhoodKommuner(sources.neighbourhoods, area.code).map((kommun) => kommunNames.get(kommun) || '');
            if (!own) return;
            const others = around.filter(Boolean).sort((a, b) => a.localeCompare(b, 'sv'));
            const key = [own, ...others].join('|');
            if (!bases.has(key)) bases.set(key, { key, own, others, numbers: [] });
            bases.get(key).numbers.push(index + 1);
        });
        return [...bases.values()];
    }, [sources, areas]);

    const names = areas.map((area) => area.name);
    const kommuner = [...new Set(areas.map((area) => area.kommun).filter(Boolean))];
    const totals = areas.map((area) => homes[area.code]);
    const total = totals.length && totals.every((value) => Number.isFinite(value)) ? totals.reduce((sum, value) => sum + value, 0) : null;

    useEffect(() => {
        // A preview over the map keeps the view's title, and is counted by the card (compare.preview).
        if (!areas.length || preview) return undefined;
        document.title = `${joinNames(names, swedish)} – hempejl`;
        track('compare_page.view', { count: areas.length });
        return () => { document.title = 'hempejl'; };
        // One view per comparison.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areas.length, codesKey]);

    const goToAll = useCallback((path, action) => {
        const picked = areas.map(({ code, name, kommun }) => ({ code, name, kommun }));
        if (path === '/') noteComparedHomesShown();
        // Previewed over a view: kartvy's map buttons pick the areas on that map (or just close), listvy's homes
        // button makes them its search; anything else goes to the other view as on the page.
        if (preview && path === '/kartvy' && onShowOnMap) {
            track('compare_page.action', { action, count: areas.length, preview: true });
            if (action === 'map') onShowOnMap(picked);
            else onClosePreview?.();
            return;
        }
        if (preview && path === '/' && onShowHomes) {
            track('compare_page.action', { action, count: areas.length, preview: true });
            onShowHomes(picked);
            return;
        }
        saveAreaPagePicks(picked);
        track('compare_page.action', { action, count: areas.length, ...(preview ? { preview: true } : {}) });
        navigate(path);
    }, [areas, navigate, preview, onShowOnMap, onShowHomes, onClosePreview]);
    const goToOne = useCallback((area, path, action) => {
        if (path === '/') noteComparedHomesShown();
        saveAreaPagePick({ code: area.code, name: area.name, kommun: area.kommun });
        track('compare_page.action', { action, count: areas.length, regso: area.code, ...(preview ? { preview: true } : {}) });
        navigate(path);
    }, [areas.length, navigate, preview]);
    const handleShare = useCallback(() => {
        shareComparison({ codes: areas.map((area) => area.code), names, source: preview ? 'preview' : 'page' }).then((result) => {
            if (result !== 'copied') return;
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_MS);
        });
    }, [areas, names, preview]);
    const openOther = useCallback((other) => {
        track('compare_page.action', { action: 'neighbour', count: areas.length, to: other.code });
        navigate(areaPagePath(other.code, other.name));
    }, [areas.length, navigate]);

    let body;
    if (loadError) {
        body = (
            <div className="area-page-message">
                <h1>{swedish ? 'Jämförelsen gick inte att ladda' : 'The comparison could not be loaded'}</h1>
                <p>{swedish ? 'Försök igen om en stund.' : 'Please try again in a moment.'}</p>
                <button type="button" className="area-page-btn is-primary" onClick={() => setAttempt((n) => n + 1)}>
                    {swedish ? 'Försök igen' : 'Try again'}
                </button>
            </div>
        );
    } else if (!sources) {
        body = (
            <div className="area-page-hero is-loading" aria-busy="true" aria-label={swedish ? 'Laddar jämförelsen' : 'Loading the comparison'}>
                <div className="area-page-hero-text">
                    <i className="area-page-skeleton" style={{ width: '40%' }} />
                    <i className="area-page-skeleton is-title" />
                    <i className="area-page-skeleton" style={{ width: '70%' }} />
                </div>
                <div className="area-page-map" />
            </div>
        );
    } else if (areas.length < 2) {
        body = (
            <div className="area-page-message">
                <h1>{swedish ? 'Vi hittar inte områdena' : "We can't find those areas"}</h1>
                <p>{swedish ? 'Länken kan vara fel eller gammal. Jämför områden på kartan istället.' : 'The link may be wrong or old. Compare areas on the map instead.'}</p>
                <Link className="area-page-btn is-primary" to="/kartvy">{swedish ? 'Till kartan' : 'To the map'}</Link>
            </div>
        );
    } else {
        const allHomesLabel = total
            ? (swedish ? `Visa alla ${total} bostäder` : `Show all ${total} homes`)
            : (swedish ? 'Visa bostäderna' : 'Show the homes');
        body = (
            <>
                <section className="area-page-hero">
                    <div className="area-page-hero-text">
                        <span className="area-page-kicker">
                            {kommuner.length === 1
                                ? (swedish ? `${areas.length} områden i ${kommuner[0]}` : `${areas.length} areas in ${kommuner[0]}`)
                                : (swedish ? `${areas.length} områden` : `${areas.length} areas`)}
                        </span>
                        <h1 className="compare-title">{joinNames(names, swedish)}</h1>
                        <p className="area-page-summary">
                            {swedish
                                ? 'Sida vid sida: betyg, priser och livet i områdena, och bostäderna som är till salu där nu.'
                                : 'Side by side: scores, prices and life in the areas, and the homes for sale there now.'}
                        </p>
                        <ol className="compare-key">
                            {areas.map((area, index) => (
                                <li key={area.code}>
                                    <span className="compare-num">{index + 1}</span>
                                    <span className="compare-key-name">{area.name}</span>
                                    {area.score != null && (
                                        <span className="compare-score" style={{ background: area.colour, color: scoreTextOn(area.colour) }}>
                                            {formatScore(area.score, swedish)}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ol>
                        <div className="area-page-actions">
                            {total !== 0 && (
                                <a className="area-page-btn is-primary" href="/" onClick={(event) => { event.preventDefault(); goToAll('/', 'listings'); }}>
                                    {allHomesLabel}
                                </a>
                            )}
                            <a className="area-page-btn is-secondary" href="/kartvy" onClick={(event) => { event.preventDefault(); goToAll('/kartvy', 'map'); }}>
                                {swedish ? 'Se dem på kartan' : 'See them on the map'}
                            </a>
                            <button type="button" className={`area-page-btn is-quiet${copied ? ' is-copied' : ''}`} onClick={handleShare}>
                                {SHARE_ICON}
                                {copied ? (swedish ? 'Länk kopierad!' : 'Link copied!') : (swedish ? 'Dela' : 'Share')}
                            </button>
                        </div>
                    </div>
                    <CompareMap shapes={shapes} areas={areas} swedish={swedish} onPickOther={openOther} />
                </section>

                <section className="compare-table-wrap" aria-label={swedish ? 'Områdena sida vid sida' : 'The areas side by side'}>
                    <div className="compare-table-scroll" ref={scrollRef}>
                        <table className="compare-table" style={{ '--compare-cols': areas.length }}>
                            <thead>
                                <tr>
                                    <th className="compare-corner" aria-hidden="true" />
                                    {areas.map((area, index) => (
                                        <th key={area.code} scope="col" data-compare-col={area.code}
                                            className={`compare-area${columnDrag.dragging === area.code ? ' is-dragging' : ''}`}>
                                            <div className="compare-area-box">
                                                <span className="compare-area-top">
                                                    <span className="compare-num">{index + 1}</span>
                                                    {area.score != null && (
                                                        <span className="compare-score" style={{ background: area.colour, color: scoreTextOn(area.colour) }}>
                                                            {formatScore(area.score, swedish)}
                                                        </span>
                                                    )}
                                                    {trends[area.code] && (
                                                        <span className={`compare-trend${trends[area.code].change < 0 ? ' is-down' : ''}`}>
                                                            {formatTrend(trends[area.code], swedish, { long: true })}
                                                        </span>
                                                    )}
                                                </span>
                                                <a className="compare-area-name is-handle" href={areaPagePath(area.code, area.name)}
                                                    title={swedish ? 'Dra för att flytta' : 'Drag to move'}
                                                    {...columnDrag.handleProps(area.code)}
                                                    onClick={(event) => { event.preventDefault(); goToOne(area, areaPagePath(area.code, area.name), 'area'); }}>
                                                    {area.name}
                                                </a>
                                                <span className="compare-area-word">
                                                    {[area.word ? `${area.word} ${swedish ? 'område' : 'area'}` : (swedish ? 'Inget betyg' : 'No score'), area.kommun]
                                                        .filter(Boolean).join(' · ')}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARE_ROWS.map((row) => (
                                    <tr key={row.key}>
                                        <th scope="row" className="compare-label">{row[swedish ? 'sv' : 'en']}</th>
                                        {areas.map((area) => {
                                            const fact = area.facts[row.key];
                                            const top = tops[row.key]?.has(area.code);
                                            return (
                                                <td key={area.code} className={`compare-cell${columnDrag.dragging === area.code ? ' is-dragging' : ''}`}>
                                                    <span className="compare-fact">
                                                        <b>{fact?.value || '–'}</b>
                                                        {fact?.score != null && <i className="compare-dot" style={{ background: fact.color }} />}
                                                        {top && <span className="compare-top">{row.top[swedish ? 'sv' : 'en']}</span>}
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                <tr className="is-homes">
                                    <th scope="row" className="compare-label">{swedish ? 'Till salu nu' : 'For sale now'}</th>
                                    {areas.map((area) => {
                                        const count = homes[area.code];
                                        return (
                                            <td key={area.code} className={`compare-cell${columnDrag.dragging === area.code ? ' is-dragging' : ''}`}>
                                                {count === 0 ? (
                                                    <span className="compare-homes-none">{swedish ? 'Inget till salu just nu' : 'Nothing for sale right now'}</span>
                                                ) : (
                                                    <a className="area-page-btn is-primary compare-homes-btn" href="/"
                                                        onClick={(event) => { event.preventDefault(); goToOne(area, '/', 'listings_one'); }}>
                                                        {count
                                                            ? (swedish ? `Visa ${count} ${count === 1 ? 'bostad' : 'bostäder'}` : `Show ${count} ${count === 1 ? 'home' : 'homes'}`)
                                                            : (swedish ? 'Visa bostäder' : 'Show homes')}
                                                    </a>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {scoreBases.length > 0 && (
                        <div className="compare-basis">
                            <h2 className="compare-basis-title">{swedish ? 'Betyget jämförs med' : 'The score is compared with'}</h2>
                            <ul className="compare-basis-list">
                                {scoreBases.map((basis) => (
                                    <li key={basis.key}>
                                        <span className="compare-basis-nums">
                                            {basis.numbers.map((number) => <span key={number} className="compare-num">{number}</span>)}
                                        </span>
                                        <span className="compare-basis-text">
                                            <b>{kommunName(basis.own, swedish)}</b>
                                            {basis.others.length > 0 && (swedish
                                                ? ` och kommunerna runt: ${listNames(basis.others, true)}`
                                                : ` and the municipalities around it: ${listNames(basis.others, false)}`)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>

                <section className="compare-cta">
                    <div>
                        <h2>{swedish ? 'Hitta ditt nästa hem här' : 'Find your next home here'}</h2>
                        <p>
                            {total
                                ? (swedish ? `${total} bostäder till salu i ${joinNames(names, true)} just nu.` : `${total} homes for sale in ${joinNames(names, false)} right now.`)
                                : (swedish ? 'Se bostäderna till salu i områdena, eller jämför fler områden på kartan.' : 'See the homes for sale in the areas, or compare more areas on the map.')}
                        </p>
                    </div>
                    <div className="area-page-actions">
                        {total !== 0 && (
                            <a className="area-page-btn is-light" href="/" onClick={(event) => { event.preventDefault(); goToAll('/', 'listings_cta'); }}>
                                {allHomesLabel}
                            </a>
                        )}
                        <a className="area-page-btn is-ghost" href="/kartvy" onClick={(event) => { event.preventDefault(); goToAll('/kartvy', 'map_cta'); }}>
                            {swedish ? 'Jämför fler på kartan' : 'Compare more on the map'}
                        </a>
                    </div>
                </section>

                <footer className="area-page-foot">
                    <span>
                        {swedish
                            ? 'Betyget visar hur ett område står sig mot områdena runt omkring, från 1 till 6. '
                            : 'The score shows how an area compares with the areas around it, from 1 to 6. '}
                        <Link to="/faq">{swedish ? 'Vad betyder betyget?' : 'What does the score mean?'}</Link>
                    </span>
                </footer>
            </>
        );
    }

    return (
        <div className="area-page compare-page">
            <div className="area-page-inner">{body}</div>
        </div>
    );
}
